// QR code del negozio: anteprima, download PNG e stampa (biglietti o cartello da banco).
// Il QR porta al link della bottega con attribuzione (?ref) e provenienza "qr".
import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { Download, Printer, QrCode } from 'lucide-react';
import { Button } from '../ui/button';

const SIZE = 640;

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

// Disegna il QR (correzione d'errore alta) con il logo della bottega al centro
async function drawQr(canvas, url, { dark, logoUrl }) {
  await QRCode.toCanvas(canvas, url, {
    errorCorrectionLevel: 'H',
    margin: 2,
    width: SIZE,
    color: { dark, light: '#ffffff' },
  });
  if (!logoUrl) return;
  try {
    const img = await loadImage(logoUrl);
    const ctx = canvas.getContext('2d');
    const box = SIZE * 0.22;
    const x = (SIZE - box) / 2;
    ctx.fillStyle = '#ffffff';
    const r = 16;
    ctx.beginPath();
    ctx.moveTo(x + r, x);
    ctx.arcTo(x + box, x, x + box, x + box, r);
    ctx.arcTo(x + box, x + box, x, x + box, r);
    ctx.arcTo(x, x + box, x, x, r);
    ctx.arcTo(x, x, x + box, x, r);
    ctx.fill();
    const pad = box * 0.1;
    const scale = Math.min((box - 2 * pad) / img.width, (box - 2 * pad) / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, (SIZE - w) / 2, (SIZE - h) / 2, w, h);
    canvas.toDataURL(); // verifica che il canvas non sia "sporcato" da un'immagine senza CORS
  } catch (_) {
    // Logo non disponibile: si ridisegna il QR semplice
    await QRCode.toCanvas(canvas, url, { errorCorrectionLevel: 'H', margin: 2, width: SIZE, color: { dark, light: '#ffffff' } });
  }
}

const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

function printSheet({ kind, dataUrl, name, motto, url, color }) {
  const w = window.open('', '_blank');
  if (!w) {
    toast.error('Il browser ha bloccato la finestra di stampa: consenti i pop-up per questo sito');
    return;
  }
  const shortUrl = url.replace(/^https?:\/\//, '').replace(/\?.*$/, '');
  const card = `
    <div class="card">
      <img src="${dataUrl}" alt="QR" />
      <div class="txt">
        <div class="name">${escapeHtml(name)}</div>
        ${motto ? `<div class="motto">${escapeHtml(motto)}</div>` : ''}
        <div class="cta">Inquadra e scopri le mie creazioni</div>
        <div class="url">${escapeHtml(shortUrl)}</div>
        <div class="rfx">Bottega su Rarifex · Pagamento protetto</div>
      </div>
    </div>`;
  const body = kind === 'cards'
    ? `<div class="sheet">${Array.from({ length: 10 }).map(() => card).join('')}</div>`
    : `<div class="sign">
        <div class="sname">${escapeHtml(name)}</div>
        ${motto ? `<div class="smotto">${escapeHtml(motto)}</div>` : ''}
        <img src="${dataUrl}" alt="QR" />
        <div class="scta">Inquadra il codice per vedere tutte le creazioni<br/>e ordinare online</div>
        <div class="url">${escapeHtml(shortUrl)}</div>
        <div class="rfx">Bottega su Rarifex · Pagamento protetto</div>
      </div>`;
  w.document.write(`<!doctype html><html lang="it"><head><meta charset="utf-8"><title>${escapeHtml(name)} – QR</title>
  <style>
    @page { size: A4; margin: 10mm; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: Inter, Helvetica, Arial, sans-serif; color: #0f172a; }
    .sheet { display: grid; grid-template-columns: repeat(2, 85mm); grid-auto-rows: 55mm; gap: 0; justify-content: center; }
    .card { border: 0.2mm dashed #cbd5e1; padding: 4mm; display: flex; gap: 4mm; align-items: center; }
    .card img { width: 40mm; height: 40mm; }
    .txt { display: flex; flex-direction: column; gap: 1mm; min-width: 0; }
    .name { font-weight: 700; font-size: 12pt; color: ${color}; line-height: 1.1; }
    .motto { font-size: 7.5pt; font-style: italic; color: #475569; }
    .cta { font-size: 8pt; margin-top: 1.5mm; }
    .url { font-size: 6.5pt; color: #64748b; word-break: break-all; }
    .rfx { font-size: 6pt; color: #94a3b8; margin-top: 1mm; }
    .sign { width: 148mm; margin: 10mm auto; border: 0.6mm solid ${color}; border-radius: 6mm; padding: 12mm; text-align: center; }
    .sname { font-weight: 800; font-size: 26pt; color: ${color}; }
    .smotto { font-style: italic; color: #475569; margin-top: 2mm; font-size: 12pt; }
    .sign img { width: 90mm; height: 90mm; margin: 8mm auto 4mm; display: block; }
    .scta { font-size: 13pt; line-height: 1.4; }
    .sign .url { font-size: 10pt; margin-top: 3mm; }
    .sign .rfx { font-size: 9pt; margin-top: 4mm; }
    @media screen { body { background: #f1f5f9; padding: 10mm; } .sheet, .sign { background: #fff; } }
  </style></head><body>${body}
  <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };<\/script>
  </body></html>`);
  w.document.close();
}

export default function ShopQrCode({ url, slug, name, motto, logoUrl, color = '#0f172a' }) {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!canvasRef.current || !url) return;
      await drawQr(canvasRef.current, url, { dark: color, logoUrl });
      if (!active) return;
      try { setDataUrl(canvasRef.current.toDataURL('image/png')); } catch (_) { setDataUrl(null); }
    })();
    return () => { active = false; };
  }, [url, color, logoUrl]);

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `qr-${slug || 'negozio'}.png`;
    a.click();
  };

  return (
    <div className="mt-4 pt-4 border-t" data-testid="shop-qr">
      <p className="text-sm font-semibold text-slate-800 flex items-center gap-2"><QrCode className="w-4 h-4 text-[#008080]" /> QR code del negozio</p>
      <p className="text-xs text-slate-500 mt-1">Da stampare su biglietti, etichette dei pacchi o al banco dei mercatini: porta dritto al tuo negozio.</p>
      <div className="mt-3 flex items-center gap-4">
        <canvas ref={canvasRef} width={SIZE} height={SIZE} className="w-28 h-28 rounded-lg border bg-white" aria-label="QR code del negozio" />
        <div className="flex flex-col gap-2">
          <Button type="button" variant="outline" size="sm" onClick={download} disabled={!dataUrl} data-testid="shop-qr-download">
            <Download className="w-4 h-4 mr-1" /> Scarica PNG
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={!dataUrl} onClick={() => printSheet({ kind: 'cards', dataUrl, name, motto, url, color })} data-testid="shop-qr-cards">
            <Printer className="w-4 h-4 mr-1" /> Biglietti (10 per A4)
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={!dataUrl} onClick={() => printSheet({ kind: 'sign', dataUrl, name, motto, url, color })} data-testid="shop-qr-sign">
            <Printer className="w-4 h-4 mr-1" /> Cartello da banco
          </Button>
        </div>
      </div>
    </div>
  );
}
